'use client';

import {
  BodyLinkV3,
  LayoutGrid,
  TypographyRhythm,
  TypographyV3,
} from '@jouzen/ecom-components';

import CanadaTerms from './_components/CanadaTerms';

const Content = (): JSX.Element => {
  return (
    <LayoutGrid>
      <TypographyRhythm className="col-main">
        <TypographyV3 Element="h1" variant="h1">
          Terms of Use
        </TypographyV3>
        <TypographyV3>
          <em>Last update: May 18, 2018</em>
        </TypographyV3>
        <TypographyV3>
          Welcome and thank you for your interest in Oura! You are reviewing
          these Terms of Use because you use or have purchased, or are
          considering purchasing or using, the Oura Services (the "Oura
          Services"), consisting of a smart ring or other electronic device (the
          "Oura Product") to be used with the Oura mobile application (the
          "Mobile App") and related website at ouraring.com (the "Website")
        </TypographyV3>
        <TypographyV3>
          THESE TERMS OF USE ARE A LEGAL AGREEMENT BETWEEN YOU AND OURA. PLEASE
          READ THEM THROUGH CAREFULLY BEFORE USING ANY OURA SERVICES. By using
          any Oura Services you signify your agreement to these Terms of Use
          (the "Terms of Use"). If you do not agree to these Terms of Use, you
          may not use the Oura Services and should not purchase the Oura
          Services.
        </TypographyV3>
        <TypographyV3>
          The Oura Services have been developed and are owned by Oura Health Oy
          which is referred to as Oura, we, us or our in these Terms of Use. You
          are referred to as "you" or "your" in these Terms of Use.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Support and Questions
        </TypographyV3>
        <TypographyV3>
          If you have any questions about the installation or use of the Oura
          Services or have technical difficulties, you will find FAQs on our
          Website at ouraring.com or you may e-mail us at
          <strong>support@ouraring.com</strong>
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Changes to Services and Terms
        </TypographyV3>
        <TypographyV3>
          We may change or discontinue, in whole or in part, the Oura Services
          at any time without notice. You acknowledge that Oura is not liable to
          you or to any third party for any such action.
        </TypographyV3>
        <TypographyV3>
          We may modify these Terms of Use from time to time. The most current
          Terms of Use will be available on the Oura home page and the date of
          the latest update is indicated at the top of these Terms of Use. We
          may communicate major changes with a special notice on the Oura
          Services or by email. You accept such modified Terms of Use by
          continuing to use of the Oura Services.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Requirements for Use of the Oura Services
        </TypographyV3>
        <TypographyV3>
          You represent and warrant that you are aged 18 years or older and have
          the legal authority to accept these Terms of Use on your own behalf or
          any party you represent. Please also refer to the Precautions in these
          Terms of Use.
        </TypographyV3>
        <TypographyV3>
          The full Oura experience requires the Oura Product, Internet access to
          the Mobile App and Website, and other required software (if any).
          Periodic updates to any of these elements may be required for improved
          performance, and the performance of the whole may be affected by the
          performance of any of these elements. You are responsible for
          obtaining your own Internet access and mobile device to access the
          Oura Services.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Precautions
        </TypographyV3>
        <TypographyV3>
          Please note that Oura Services are not intended to diagnose, treat,
          cure, or prevent any disease or medical condition. The information and
          guidance in the Oura Services are there for informational purposes
          only and can not replace the services of health professionals or
          physicians. You should always consult a physician before making any
          changes to your sleep or activity based on information or guidance of
          the Oura Services, or if you have any questions regarding a medical
          condition. Never disregard professional medical advice or delay in
          seeking it because of something you have read on or through the Oura
          Services. We are not responsible for any health problems that may
          result from information or guidance you learn about through the Oura
          Services. If you make any change to your sleep or activity based on
          the Oura Services, you agree that you do so fully at your own risk. It
          is important to be sensitive to your body's responses. For example, if
          you feel unexpected, repeating or long term pain, fatigue or
          discomfort due to having made changes to your sleep or activity, it is
          recommended that you consult a physician before continuing with such
          changes. The information and guidance in the Oura Services may be
          misleading if your physiological functions and responses differ
          significantly from population averages due to medical conditions or
          rare natural differences.
        </TypographyV3>
        <TypographyV3>
          Please be cautious that the Oura Product you are wearing does not get
          caught on fixed structures or heavy objects when moving yourself or
          moving said heavy objects.
        </TypographyV3>
        <TypographyV3>
          <strong>
            If you experience redness or skin irritation on your finger due to
            Oura Product, remove it immediately. If symptoms persist longer than
            2-3 days of not using your Oura Product, please contact a
            dermatologist.
          </strong>
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Corporate Contact Info
        </TypographyV3>
        <TypographyV3>
          Mailing Address: Elektroniikkatie 10, 90590 Oulu, Finland
        </TypographyV3>
        <TypographyV3>
          Phone: +358-50-4668390 (Please note calls to this number cannot reach
          customer service. For questions on support, orders, returns or product
          information, please email us
          <strong>support@ouraring.com</strong> to more quickly have your needs
          handled).
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Privacy Policy
        </TypographyV3>
        <TypographyV3>
          The Oura Services are subject to the Oura Privacy Policy, which can be
          found at{' '}
          <BodyLinkV3 color="inherit" href="/privacy-policy">
            ouraring.com/privacy-policy
          </BodyLinkV3>
        </TypographyV3>
        <TypographyV3 Element="h3" variant="h4">
          Limited License and Ownership of the Oura Services
        </TypographyV3>
        <TypographyV3>
          You are hereby granted a non-exclusive, revocable and nontransferable
          license to use the Oura Services in accordance with these Terms of
          Use.
        </TypographyV3>
        <TypographyV3>
          Oura or its licensors own all title and rights to the Oura Services,
          including, but is not limited, to all copyrights, trademarks, know-how
          and other intellectual property rights included therein. You may not
          reproduce (whether by linking, framing or any other method), transfer,
          distribute, store, modify, decompile, disassemble, or create
          derivative works of, publicly display, or commercially exploit any
          part of the Oura Services except as necessary to display, download or
          print (without modification) for your own non-commercial use. All
          rights in the Oura Services not expressly granted to you by Oura are
          retained by Oura and its licensors. Violation of any of these Terms of
          Use gives Oura sole discretion to refuse or terminate access to the
          Oura Services effective immediately. In such an event you are obliged
          to immediately destroy any copies you have made of any portion of the
          Oura Services.
        </TypographyV3>
        <TypographyV3>
          You may propose to Oura modifications or improvements to all or any
          part of the Oura Services. By choosing to disclose such proposal to
          Oura, you hereby grant Oura all title and ownership and intellectual
          property rights to such proposal.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Indemnity
        </TypographyV3>
        <TypographyV3>
          You agree to defend, indemnify and hold Oura and its partners,
          affiliates, service providers, licensors, officers, directors,
          employees and agents harmless from and against any claims, actions or
          demands, including but not limited to reasonable legal and accounting
          fees, alleging or resulting from: (a) your violation of these Terms of
          Use; or (b) your violation of Oura's intellectual property rights, any
          third party rights or any applicable law when using the Oura Services.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Local Law and Export Control
        </TypographyV3>
        <TypographyV3>
          In relation to your purchase and use of the Oura Services, you agree
          to comply with the laws of your local jurisdiction as well as with any
          export restrictions of the United States and certain other countries
          on products or information.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Third Party Services
        </TypographyV3>
        <TypographyV3>
          To increase value to our users, we may provide links or references to
          third party websites or services within the Oura Services. We have no
          control of such websites or services and do not assume any
          responsibility or liability for any damage or loss of any kind for or
          due to their content, functionality, or practices. If you decide to
          access these third party websites or services, you do so at your own
          risk. Oura suggests that before using these third party websites or
          services you read their terms of use and privacy policies (if any).
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Content and Warranty Disclaimer
        </TypographyV3>
        <TypographyV3>
          EXCEPT FOR THE LIMITED WARRANTY FOR THE OURA PRODUCT SET FORTH BELOW,
          THE OURA SERVICES ARE PROVIDED BY OURA AND ITS AFFILIATES "AS IS."
        </TypographyV3>
        <TypographyV3>
          NEITHER OURA NOR ITS PARTNERS, SUPPLIERS, OR AFFILIATES MAKE ANY
          REPRESENTATIONS OR WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, AS TO
          THE OPERATION OF THE OURA SERVICES (INCLUDING THE OURA PRODUCTS), ITS
          CONTENTS, OR ANY INFORMATION MADE AVAILABLE BY OR THROUGH THE OURA
          SERVICES. IN ADDITION, OURA AND ITS PARTNERS, SUPPLIERS AND AFFILIATES
          DISCLAIM ALL WARRANTIES WITH RESPECT TO THE OURA SERVICES (INCLUDING
          THE OURA PRODUCTS), EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO
          THE IMPLIED WARRANTIES OF MERCHANTABILITY, TITLE, FITNESS FOR A
          PARTICULAR PURPOSE AND NON-INFRINGEMENT. FURTHERMORE, OURA DOES NOT
          WARRANT THAT USE OF THE OURA SERVICES WILL BE UNINTERRUPTED, AVAILABLE
          AT ANY TIME OR FROM ANY PARTICULAR LOCATION, SECURE OR ERROR-FREE,
          THAT DEFECTS WILL BE CORRECTED, OR THAT THE OURA SERVICES (INCLUDING
          ANY OURA PRODUCT) ARE FREE OF VIRUSES OR OTHER POTENTIALLY HARMFUL
          COMPONENTS.
        </TypographyV3>
        <TypographyV3>
          LIMITED WARRANTY FOR THE OURA PRODUCT, FOR EXAMPLE, THE RING. OURA
          WARRANTS TO THE ORIGINAL END USER PURCHASER THAT THE OURA PRODUCT IS
          FREE FROM DEFECTS IN MATERIAL AND WORKMANSHIP FOR TWO (2) YEARS FROM
          THE DATE OF PURCHASE. If your local law in effect at the time of
          purchase requires a warranty period longer than two (2) years, this
          warranty shall be extended to the extent required by such law. Within
          the warranty period, Oura shall repair or replace at no charge to you
          any components of the Oura Product that fail the limited warranty
          provided. You shall be responsible for any related transportation
          charges. Replacement products may be new or refurbished at our
          discretion. This limited warranty does not apply to (i) normal wear
          and tear, including scratches and dents; (ii) consumable parts
          included in the Oura Product, such as batteries, unless product damage
          has occurred due to a defect in materials or workmanship; (iii) damage
          resulting from your failure to use the Oura Product in accordance with
          the instructions accompanying the Oura Product or available at the
          Website; (iv) damage resulting from an accident, flood, fire, misuse,
          or abuse; (v) damage resulting from service performed, or damage
          resulting from tampering or alterations to the Oura Product, by anyone
          not authorized by Oura; or (vi) use of the Oura Product with any other
          application or software than the Mobile App.
        </TypographyV3>
        <TypographyV3>
          Oura retains the exclusive right to repair or replace the Oura
          Product, or offer a full refund, at its sole discretion. Such remedy
          shall be your sole and exclusive remedy for any breach of warranty.
          Warranty repairs and replacements have a new warranty which is the
          longer of ninety (90) days or the balance of the original two year
          warranty.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Limitation of Liability
        </TypographyV3>
        <TypographyV3>
          IN NO EVENT WILL OURA OR ANY OF ITS AFFILIATES BE LIABLE FOR DIRECT,
          INDIRECT, INCIDENTAL, PUNITIVE OR CONSEQUENTIAL DAMAGES (INCLUDING
          WITHOUT LIMITATION THOSE RESULTING FROM LOST PROFITS, LOST DATA OR
          BUSINESS INTERRUPTION) ARISING OUT OF THE USE, INABILITY TO USE, OR
          THE RESULTS OF USE OF THE OURA SERVICES, WHETHER SUCH DAMAGES ARE
          BASED ON WARRANTY, CONTRACT, TORT OR ANY OTHER LEGAL THEORY AND
          WHETHER OR NOT OURA HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGES. THE AGGREGATE LIABILITY OF OURA, ARISING FROM OR RELATING TO
          THE OURA SERVICES (REGARDLESS OF THE FORM OF ACTION OR CLAIM, E.G.
          CONTRACT, WARRANTY, TORT, STRICT LIABILITY, NEGLIGENCE, OR ANY OTHER
          LEGAL THEORY) IS LIMITED TO $100. OURA'S AFFILIATES, PROVIDERS AND
          PARTNERS HAVE NO LIABILITY WHATSOEVER ARISING FROM THE WEBSITE. In
          some locations applicable law may not allow certain of the limitations
          described above, in which case such limitations will apply to the
          maximum extent allowed by such applicable law.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Trademarks and Patents
        </TypographyV3>
        <TypographyV3>
          OURA logo, O logo and OURA are trademarks or registered trademarks of
          Oura Health Ltd. Patented and patents pending. © Oura Health Oy
          (Oura) 2014-2018. All rights reserved. You are not granted, by
          implication or otherwise, any license or right to use any marks
          appearing on Oura Services. One or more patents owned by Oura (or
          patents licensed from third parties) apply to the Oura Products and to
          the features and services accessible via the Oura Services maintained
          by Oura.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          Governing Law and Disputes
        </TypographyV3>
        <TypographyV3>
          The United Nations Convention on Contracts for the International Sale
          of Goods shall not apply to these terms of Use or your purchase of any
          element of the Oura Services.
        </TypographyV3>
        <TypographyV3>
          If you reside in the United States, these Terms of Use shall be
          governed by and construed in accordance with the laws of the State of
          California, without regard to the principles of conflicts of law of
          any jurisdiction. If you reside in any other country, these Terms of
          Use shall be governed by and construed in accordance with the laws of
          Finland, without regard to the principles of conflicts of law of any
          jurisdiction.
        </TypographyV3>
        <TypographyV3>
          Both parties shall use their best efforts to settle by amicable
          negotiations any disputes which may occur between them arising out of
          or relating to these Terms of Use; the existence, validity,
          termination, interpretation of any term hereof; and disputes regarding
          your use of the Oura Services. If the parties fail to reach an
          amicable settlement, either party may refer such dispute to binding
          arbitration. If you reside in the USA, (A) the arbitration will be
          held in accordance with the International Arbitration Rules of the
          American Arbitration Association ("AAA") in effect at the time of the
          arbitration (the "AAA Arbitration Rules"); (B) the AAA shall be the
          appointing authority and responsible for administering any arbitration
          hereunder in accordance with the AAA Arbitration Rules; and (C) the
          place of arbitration shall be in San Francisco, California.
        </TypographyV3>
        <TypographyV3>
          If you reside in any other country or territory, (1) the arbitration
          will be held in accordance with the Arbitration Rules of the
          International Chamber of Commerce of Finland ("ICC") in effect at the
          time of the arbitration (the "ICC Arbitration Rules"); (2) the ICC
          shall be the appointing authority and responsible for administering
          any arbitration hereunder in accordance with the ICC Arbitration
          Rules; and (3) the place of arbitration shall be in Helsinki, Finland.
        </TypographyV3>
        <TypographyV3>
          In either case, the arbitration shall be conducted by a single
          arbitrator who shall be a professional, legal or otherwise, but shall
          not be, or have previously been associated with either party (the
          "Arbitrator"). The arbitral award shall be final, binding and
          non-appealable. The Arbitrator's award must be reasoned and issued in
          writing within thirty (30) days of the hearing, unless otherwise
          agreed to by Oura and you.
        </TypographyV3>
        <TypographyV3>
          Notwithstanding the foregoing, in recognition of the irreparable harm
          that a breach by you of Oura's intellectual property rights would
          cause, Oura may seek an injunction against such violation or breach in
          a court of competent jurisdiction.
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h3">
          General
        </TypographyV3>
        <TypographyV3>
          English language shall govern all documents, notices, and
          interpretations of these Terms of Use. Sections titled Precautions,
          Indemnity, Content and Warranty Disclaimer, Limitation of Liability
          and Governing Law and Disputes shall survive and remain in effect
          after your license to use the Oura Services has terminated for any or
          no reason.
        </TypographyV3>
        <TypographyV3>
          Oura's failure to exercise or enforce any right or provision of these
          Terms of Use will not constitute a waiver of such right or provision
          unless acknowledged and agreed to by Oura in writing. These Terms of
          Use constitute the entire agreement between you and Oura with respect
          to the subject matter herein and supersede any and all prior or
          contemporaneous oral or written agreements. You may not assign this
          agreement to any other party and any attempt to do so is void.
        </TypographyV3>
        <TypographyV3>
          Oura Health Oy, Elektroniikkatie 10, FI-90590 Oulu, FINLAND
        </TypographyV3>
        <TypographyV3 Element="h2" variant="h1">
          Terms of Delivery
        </TypographyV3>
        <TypographyV3>
          <em>Last update: January 10, 2018</em>
        </TypographyV3>
        <TypographyV3>
          Oura shop base country is Finland. This may mean that your payment can
          be subject to foreign transaction if credit card is used. Please check
          for more information with your card issuing bank. Rings are shipped
          from EU. Oura delivers according to Incoterms® rules 2010 as
          mentioned below.
        </TypographyV3>
        <TypographyV3 Element="h3" variant="h3">
          The European Union and USA
        </TypographyV3>
        <TypographyV3>DDP – Delivered Duty Paid</TypographyV3>
        <TypographyV3>
          Oura is responsible for delivering the goods to the named place in the
          country of the buyer, and pays all costs in bringing the goods to the
          destination including import duties and taxes.
        </TypographyV3>
        <CanadaTerms />
        <TypographyV3>DAP – Delivered at Place</TypographyV3>
        <TypographyV3>
          Oura pays for carriage to the named place, except for costs related to
          import clearance, and assumes all risks prior to the point that the
          goods are ready for unloading by the buyer.
        </TypographyV3>
      </TypographyRhythm>
    </LayoutGrid>
  );
};

export default Content;
